<template>
  <header class="fixed top-0 left-0 w-full pb-2 z-20 bg-white app">
    <div class="text-sm flex">
      <div id="2" class="sticky-header grow">
        <NuxtLink
          v-if="basisPaybackFeature && currentStore.paybackStore"
          :to="'/shop/payback'"
          class="p-1 flex justify-center bg-payback-light text-payback font-Payback font-bold text-xs items-center gap-2 mb-2"
        >
          <nuxt-img
            densities="x1"
            loading="lazy"
            class="min-h-[26px] pr-1 h-[26px]"
            provider="cloudflare"
            :src="'/97/27/77/05262c90ceebe43a34e6fdec70f562de6a/Payback-logo.svg'"
            :alt="'PAYBACK'"
          />
          <div>Bei jedem Einkauf °punkten.</div>
          <div class="underline">Mehr Infos</div>
        </NuxtLink>
        <div>
          <div class="store-logo-container max-h-14 h-14 bg-white mb-4">
            <NavigationStoreLogo />
          </div>
          <div v-if="!openSearchBarMobile" class="px-2">
            <div
              id="searchbox"
              class="relative border"
              :class="
                useSuggestionsOpenState().value && suggestions?.termSuggest?.length > 0
                  ? 'rounded-b-none rounded-t-lg'
                  : 'rounded-lg'
              "
            >
              <input
                id="termsuggest-input"
                v-model="suchWort"
                type="text"
                name="q"
                placeholder="Suchen nach Produkten, Marken, Web-Code & mehr..."
                @keyup.enter="handleSearch(suchWort)"
                @input="useSuggestionsOpenState().value = true"
              >
              <button
                v-if="!isListening && activeVoiceFeature"
                class="absolute right-10 top-1/2 -translate-y-1/2"
                aria-label="Starte Spracherkennung"
                @click="start"
              >
                <BasicIcon :name="'microphone'" class="inline-block w-4 text-primary" />
              </button>
              <button
                v-if="isListening && activeVoiceFeature"
                aria-label="Stoppe Spracherkennung"
                class="absolute right-10 top-1/2 -translate-y-1/2"
                @click="stop"
              >
                <BasicIcon
                  :name="'microphone'"
                  class="inline-block w-4 text-primary-dark"
                />
              </button>
              <div
                v-if="isListening"
                class="voice-notifictation shadow-2xl bg-white border-2 border-primary text-primary fixed px-4 py-1 rounded-lg top-10 left-1/2 -translate-x-1/2 text-center"
              >
                Spracherkennung<br ><strong>aktiviert</strong>
              </div>
              <div class="flex gap-2 absolute right-2 top-1/2 -translate-y-1/2">
                <button aria-label="Starte Suche" @click="handleSearch(suchWort)">
                  <BasicIcon
                    :name="'magnifying-glass-solid'"
                    class="inline-block w-5 text-primary translate-y-0.5"
                  />
                </button>
                <AccessabilityColorContrastSwitch :icon-classes="'h-4 w-4'" />
              </div>
              <div id="termsuggest"/>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ClientOnly>
      <Teleport to="#termsuggest">
        <div
          v-show="useSuggestionsOpenState().value"
          ref="suggestionsBox"
          class="flex w-full absolute right-0 top-full"
        >
          <div
            v-if="suggestions?.termSuggest?.length > 0"
            class="suggestions grid border-t-white absolute w-full bg-white py-4 border-white shadow"
            :class="{ 'grid-cols-12': suggestions?.termSuggest?.length > 0 }"
          >
            <div v-if="suggestions.termSuggest.length > 0" class="col-span-12">
              <template
                v-for="suggestion in suggestions.termSuggest"
                :key="suggestion.label"
              >
                <div>
                  <button
                    class="hover:bg-secondary px-4 py-2 w-11/12 text-left"
                    @click="editSearch(suggestion.label, 'Suchvorschlag')"
                    v-html="suggestion.highlighting"
                  />
                  <div class="arrow-icon">
                    <BasicIcon
                      class="w-4 h-4 text-primary"
                      :name="'arrow-up-left-solid'"
                      @click="editSearch(suggestion.label, 'Suchvorschlag')"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </Teleport>
    </ClientOnly>
  </header>
</template>

<script setup lang="ts">
import { onClickOutside, watchThrottled, useSpeechRecognition } from "@vueuse/core";
import { ProvideNames } from '#shared/statics';
import { inject } from "vue";

const currentStore = useCurrentStore();
const suchWort = ref("");
const props = defineProps({
  baseDir: {
    default: () => {
      return inject(ProvideNames.BASE_DIR);
    },
  },
});
const activeVoiceFeature = ref(false);
onNuxtReady(async () => {
  activeVoiceFeature.value = !iOS();
});

function iOS() {
  return (
    [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "iPod",
    ].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
}
const suggestionsBox = ref();
onClickOutside(suggestionsBox, (event) => (useSuggestionsOpenState().value = false));
const storeRef = inject(ProvideNames.CURRENT_STORE_KEY);
const updateFunction = storeRef?.updateFunction || console.error;
const lang = ref("de-DE");
const speech = useSpeechRecognition({
  lang,
  interimResults: false,
  continuous: false,
});
const { isListening, stop, result } = speech;

if (speech.isSupported.value) {
  watch(speech.result, () => {
    suchWort.value = "";
    suchWort.value = speech.result.value;
    useSuggestionsOpenState().value = true;
  });
}

function start() {
  speech.result.value = "";
  speech.start();
}

const queryFor = ref("");
const { data: suggestions } = await useLazyFetch(
  useRuntimeConfig()?.public.apiUrl + "/api/search/suggest",
  {
    query: {
      q: queryFor,
      storeId: useStoreCookie(),
      noArticles: useDevice().isMobile,
    },
  }
);

watchThrottled(
  suchWort,
  (val) => {
    console.debug("Suchwort changed:", suchWort.value);
    queryFor.value = suchWort.value;
  },
  { throttle: 250 }
);
function handleSearch(token) {
  if (token?.toLowerCase() == "#einruckeln") {
    useEasterEgg("einruckeln");
  } else if (token?.toLowerCase() == "#btdev") {
    useEasterEgg("btdev");
    setSearchWord("");
    return;
  }
  useMarker("SearchUsed");
  handleHardlinkCheck(token);
  useSuggestionsOpenState().value = false;
}

const openSearchBarMobile = ref(false);
const featureStatePayback = computed(() => useFeatureState('exsi-1556') && useCurrentStore().value.paybackStore);
const basisPaybackFeature = computed(
  () =>
    featureStatePayback.value
    && (useFeatureValue('exsi-1556').value?.name == 'basic'
      || useFeatureValue('exsi-1556').value?.name == 'redemption'
      || useFeatureValue('exsi-1556').value?.name == 'full')
);

function setSearchWord(event: any) {
  if (event == "[object Event]") {
  } else {
    suchWort.value = event;
  }
}

const editSearch = (suggestion: string, suggest?: string) => {
  useMarker("UsedSuggest" + suggest);
  suchWort.value = suggestion;
  useSearchWord().value = suggestion;
  handleHardlinkCheck(suggestion, suggest);
};

const webCodeRegEx = new RegExp("[0-9]{11}");

const handleHardlinkCheck = async (value: string, suggest?: string) => {
  if (value?.toLowerCase()?.trim() == "zentralshop1") {
    return await switchToCentralStore(updateFunction);
  } else if (value?.startsWith("e_")) {
    const { data: rawResult } = await useFetch(`/api/search/article?articleId=${value}`);
    const mappedArticle = rawResult?.value;
    if (mappedArticle?.link == undefined) {
      await navigateTo({
        path: "/shop/suche",
        query: {
          q: value,
          suggestType: suggest ? suggest : undefined,
          suggest: suggest ? "true" : undefined,
        },
      });
    } else {
      await navigateTo(
        {
          path: mappedArticle?.link?.startsWith("/shop")
            ? mappedArticle?.link
            : props.baseDir + mappedArticle?.link,
          query: {
            sq: value,
            queryType: "webcode",
          },
        },
        { external: false, redirectCode: 307 }
      );
    }
  } else if (webCodeRegEx.test(value)) {
    const { data: rawResult } = await useFetch(`/api/search/article?webcode=${value}`);
    const mappedArticle = rawResult?.value;
    if (mappedArticle?.link == undefined) {
      await navigateTo({
        path: "/shop/suche",
        query: {
          q: value,
        },
      });
    } else {
      await navigateTo(
        {
          path: mappedArticle?.link?.startsWith("/shop")
            ? mappedArticle?.link
            : props.baseDir + mappedArticle?.link,
          query: {
            sq: value,
            queryType: "webcode",
          },
        },
        { external: false, redirectCode: 307 }
      );
    }
  } else {
    const { data } = await useFetch(`/api/search/redirect/${value}`);
    if (data?.value?.redirectUrl && data?.value?.redirectUrl?.length > 0) {
      const redPath = data?.value?.redirectUrl?.split("?")?.at(0);

      const isExt = !redPath?.includes("unsere-produkte") && !redPath?.includes("suche");
      const params = new URLSearchParams(
        "?" + data?.value?.redirectUrl?.split("?")?.at(1)
      );
      params.append("sq", value);
      if (data?.value?.redirectType)
        params.append("queryType", data?.value?.redirectType);

      console.debug(
        "queries and final path",
        isExt,
        params,
        redPath + (params.size > 0 ? "?" + params.toString() : "")
      );

      await navigateTo(redPath + (params.size > 0 ? "?" + params.toString() : ""), {
        external: isExt,
        redirectCode: 307,
      });
    } else {
      await navigateTo({
        path: "/shop/suche",
        query: {
          q: value,
          suggestType: suggest ? suggest : undefined,
          suggest: suggest ? "true" : undefined,
        },
      });
      useSearchWord().value = value;
      suchWort.value = value;
    }
  }
};

onMounted(() => {
  try {
    window.addEventListener("CookiebotOnConsentReady", function (e) {
      function delete_cookie(name, path?, domain?) {
        if (get_cookie(name)) {
          document.cookie =
            name +
            "=" +
            (path ? ";path=" + path : "") +
            (domain ? ";domain=" + domain : "") +
            ";expires=Thu, 01 Jan 1970 00:00:01 GMT";
        }
      }
      function get_cookie(name) {
        return document.cookie.split(";").some((c) => {
          return c.trim().startsWith(name + "=");
        });
      }
      const prefs = {
        REQUIRED: Cookiebot.consent.necessary,
        STATISTICS: Cookiebot.consent.statistics,
        MARKETING: Cookiebot.consent.marketing,
        PRODUCT_RATINGS: Cookiebot.consent.preferences,
      };
      delete_cookie("UserCookiePermissions");
      document.cookie =
        "UserCookiePermissions=" + encodeURIComponent(JSON.stringify(prefs));
      for (const k in prefs) {
        useState("cookie" + k).value = prefs[k];
      }
    });
  } catch (error) {
    console.warn("error checking cookie permissions.", error);
  }
});
</script>

<style>
#q-downshift #q-input {
  min-height: 52px;
  padding: 12px 36px;
  background-color: white;
  z-index: 1;
}

.hidden_input {
  display: none;
}

.arrow-icon {
  cursor: pointer;
  display: contents;
}

.store-logo-dark {
  filter: hue-rotate(180deg);
}
.app .store-logo {
  min-height: 3.5rem;
}
.store-logo-dark-2 {
  filter: invert(1);
}

#termsuggest-input {
  @apply rounded-lg text-sm text-paragraph-light focus:text-primary leading-6 w-full border border-transparent outline-none truncate;
  padding: 8px 70px 9px 7px;
  min-height: unset;
  resize: none;
  height: 40px;
}
</style>
